import { Form, Input, Select, Button, Row, Col, Space, Modal, message, Tabs, Tooltip } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useEffect, useState, useRef } from "react";
import AnimateItems from "../../../../utils/AnimateItems";
import { tipospersona, nacionalidades } from "../formOptions";
import axios from "axios";
import DatoForm from "../persona/DatoForm";
import CorreoForm from "../persona/CorreoForm";
import TelefonoForm from "../persona/TelefonoForm";

const SIN_PERSONA_TOOLTIP = "Guarda primero los datos generales de la persona (pestaña General) para poder agregar esta información.";

export default function PersonaForm(props) {
  const {
    setPersonasList,
    setDatosPersona,
    setTelefonosFijos,
    setTelefonosMoviles,
    setCorreosPersona,
    personaEditing,
    setPersonaEditing,
    personaFormVisible,
    setPersonaFormVisible,
    origin,
    setFoliosPersona,
    setDomiciliosPersona,
    onPersonaCreated,
    refreshPersonasList,
    onTelefonoAdded,
    onDatoChanged,
    onCorreoAdded,
  } = props;
  const [form] = Form.useForm();
  const [infoDatos, setInfoDatos] = useState();
  const [infoTelefonos, setInfoTelefonos] = useState();
  const [infoCorreos, setInfoCorreos] = useState();
  const [isFisicaHidden, setIsFisicaHidden] = useState(true);
  const [isMoralHidden, setIsMoralHidden] = useState(true);
  const [activeTabKey, setActiveTabKey] = useState("1");
  const moralRef = useRef();
  const fisicaRef = useRef();

  // Whether there is currently a saved persona we can safely attach data to.
  const hasSavedPersona = Boolean(personaEditing);

  useEffect(() => {
    if (personaEditing) {
      Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/personas/${personaEditing}`),
        axios.get(`${process.env.REACT_APP_API_URL}/datos/persona/${personaEditing}`),
        axios.get(`${process.env.REACT_APP_API_URL}/telefonos/persona/${personaEditing}`),
        axios.get(`${process.env.REACT_APP_API_URL}/correos/persona/${personaEditing}`),
      ])
        .then(([p, d, t, c]) => {
          form.setFieldsValue(p.data[0]);
          setInfoDatos(d.data);
          setInfoTelefonos(t.data);
          setInfoCorreos(c.data);
          if (p.data[0].TipoPersonaID === 1) {
            setIsFisicaHidden(false);
            setIsMoralHidden(true);
          } else if (p.data[0].TipoPersonaID === 2) {
            setIsMoralHidden(false);
            setIsFisicaHidden(true);
          } else {
            setIsFisicaHidden(true);
            setIsMoralHidden(true);
          }
        })
        .catch((error) => {
          console.log(error);
          message.error(error.message);
        });
    } else {
      form.resetFields();
      form.setFieldsValue({ NacionalidadID: 2 });
      setInfoDatos(undefined);
      setInfoTelefonos(undefined);
      setInfoCorreos(undefined);
      setIsFisicaHidden(true);
      setIsMoralHidden(true);
      setActiveTabKey("1");
    }
  }, [personaEditing, form, personaFormVisible]);

  useEffect(() => {
    if (isFisicaHidden) {
      moralRef.current?.focus();
      form.setFieldsValue({
        Nombre: null,
        ApellidoPaterno: null,
        ApellidoMaterno: null,
      });
    } else if (isMoralHidden) {
      fisicaRef.current?.focus();
      form.setFieldsValue({
        RazonSocial: null,
      });
    }
  }, [form, isFisicaHidden, isMoralHidden]);

  const clearSelectProps = {
    style: { width: "100%" },
    allowClear: true,
    clearIcon: <CloseOutlined style={{ color: "red" }} />,
    showSearch: true,
    filterOption: (input, option) => option.children?.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0,
  };

  const onEditSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        axios
          .put(`${process.env.REACT_APP_API_URL}/personas/${personaEditing}`, values)
          .then((response) => {
            setPersonaFormVisible(false);
            message.success("Persona editada con éxito");
            if (refreshPersonasList) {
              refreshPersonasList();
            } else {
              axios
                .get(`${process.env.REACT_APP_API_URL}/personas`)
                .then((response) => {
                  setPersonasList(response.data);
                })
                .catch((error) => {
                  console.log("Error gettting personas", error);
                  message.error("Error actualizando lista de personas\n" + error.message);
                });
            }
          })
          .catch((error) => {
            console.log("Request failed", error);
            message.error("Error editando persona\n" + error.message);
          });
      })
      .catch((error) => {
        console.log("Validation error", error);
        message.error("Por favor complete los campos requeridos\n" + error.message);
      });
  };

  const onAddSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        axios
          .post(`${process.env.REACT_APP_API_URL}/personas`, values)
          .then((response) => {
            message.success("Persona agregada con éxito");

            // POST /personas returns the raw mysql OkPacket, so the new
            // row's id comes back as insertId.
            const newPersonaId = response.data?.insertId;

            if (refreshPersonasList) {
              refreshPersonasList();
            } else {
              axios
                .get(`${process.env.REACT_APP_API_URL}/personas`)
                .then((response) => {
                  setPersonasList(response.data);
                })
                .catch((error) => {
                  console.log("Error gettting personas", error);
                  message.error("Error actualizando lista de personas\n" + error.message);
                });
            }

            if (newPersonaId) {
              // Keep the modal open and switch into "edit" mode for the
              // person we just created, so the other tabs unlock and the
              // user can add their teléfonos/correos/identificaciones.
              setPersonaEditing?.(newPersonaId);
              onPersonaCreated?.(newPersonaId);
              setActiveTabKey("2");
            } else {
              console.warn(
                "No se pudo determinar el ID de la persona recién creada a partir de la respuesta del API; cerrando el formulario."
              );
              setPersonaFormVisible(false);
            }
          })
          .catch((error) => {
            console.log("Request failed", error);
            message.error("Error agregando persona\n" + error.message);
          });
      })
      .catch((error) => {
        console.log("Validation error", error);
        message.error("Por favor complete los campos requeridos\n" + error.message);
      });
  };

  const clearForm = () => {
    form.resetFields();
  };

  const tabTitle = (label) =>
    hasSavedPersona ? (
      label
    ) : (
      <Tooltip title={SIN_PERSONA_TOOLTIP}>
        <span>{label}</span>
      </Tooltip>
    );

  return (
    <>
      <Modal title="Persona" visible={personaFormVisible} onCancel={() => setPersonaFormVisible(false)} footer={null} centered width={700}>
        <Tabs activeKey={activeTabKey} onChange={setActiveTabKey}>
          <Tabs.TabPane tab="General" key="1">
            <Form form={form} layout="vertical">
              <AnimateItems delay={0.15}>
                <Form.Item name="NacionalidadID" label="Nacionalidad" rules={[{ required: true, message: "Elige una nacionalidad" }]}>
                  <Select {...clearSelectProps}>
                    {nacionalidades.map((nacionalidad) => (
                      <Select.Option key={nacionalidad.value} value={nacionalidad.value}>
                        {nacionalidad.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item name="TipoPersonaID" label="Tipo de persona" rules={[{ required: true, message: "Elige el tipo de persona" }]}>
                  <Select
                    {...clearSelectProps}
                    onChange={(value) => {
                      setTimeout(() => {
                        if (value === 1) {
                          setIsFisicaHidden(false);
                          setIsMoralHidden(true);
                        } else if (value === 2) {
                          setIsMoralHidden(false);
                          setIsFisicaHidden(true);
                        } else {
                          setIsFisicaHidden(true);
                          setIsMoralHidden(true);
                        }
                      }, 350);
                    }}
                  >
                    {tipospersona.map((tipo) => (
                      <Select.Option key={tipo.value} value={tipo.value}>
                        {tipo.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item name="Nombre" label="Nombre" rules={[{ type: "string", message: "Escribe el nombre" }]} hidden={isFisicaHidden}>
                  <Input ref={fisicaRef} />
                </Form.Item>
                <Row gutter={[8, 8]}>
                  <Col span={12}>
                    <Form.Item
                      name="ApellidoPaterno"
                      label="Apellido paterno"
                      rules={[{ type: "string", message: "Escribe el apellido paterno" }]}
                      hidden={isFisicaHidden}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="ApellidoMaterno"
                      label="Apellido materno"
                      rules={[{ type: "string", message: "Escribe el apellido materno" }]}
                      hidden={isFisicaHidden}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item
                  name="RazonSocial"
                  label="Razón social"
                  rules={[{ type: "string", message: "Escribe la razón social" }]}
                  hidden={isMoralHidden}
                >
                  <Input ref={moralRef} />
                </Form.Item>
              </AnimateItems>
            </Form>
            <div style={{ textAlign: "right" }}>
              <Space>
                <Button
                  type="primary"
                  onClick={() => {
                    personaEditing ? onEditSubmit() : onAddSubmit();
                  }}
                >
                  {personaEditing ? "Actualizar" : "Guardar"}
                </Button>
                <Button
                  onClick={() => {
                    clearForm();
                    setPersonaFormVisible(false);
                  }}
                >
                  Cancelar
                </Button>
              </Space>
            </div>
          </Tabs.TabPane>
          {/* ----------------------------- DATOS ----------------------------- */}
          <Tabs.TabPane tab={tabTitle("Identificaciones")} key="2" disabled={!hasSavedPersona}>
            <Space direction="vertical" style={{ width: "100%" }}>
              {infoDatos?.map((dato) => (
                <DatoForm
                  key={dato.DatoID}
                  dato={dato}
                  personaEditing={personaEditing}
                  type="edit"
                  setInfoDatos={setInfoDatos}
                  setDatosPersona={setDatosPersona}
                  origin={origin}
                  setFoliosPersona={setFoliosPersona}
                  setDomiciliosPersona={setDomiciliosPersona}
                />
              ))}
              <DatoForm
                personaEditing={personaEditing}
                type="add"
                setInfoDatos={setInfoDatos}
                setDatosPersona={setDatosPersona}
                origin={origin}
                setFoliosPersona={setFoliosPersona}
                setDomiciliosPersona={setDomiciliosPersona}
                onDatoChanged={onDatoChanged}
              />
            </Space>
          </Tabs.TabPane>
          {/* ----------------------------- TELEFONOS ----------------------------- */}
          <Tabs.TabPane tab={tabTitle("Teléfonos")} key="3" disabled={!hasSavedPersona}>
            <Space direction="vertical" style={{ width: "100%" }}>
              {infoTelefonos?.map((telefono) => (
                <TelefonoForm
                  key={telefono.TelefonoID}
                  telefono={telefono}
                  personaEditing={personaEditing}
                  type="edit"
                  setInfoTelefonos={setInfoTelefonos}
                  setTelefonosFijos={setTelefonosFijos}
                  setTelefonosMoviles={setTelefonosMoviles}
                />
              ))}
              <TelefonoForm
                personaEditing={personaEditing}
                type="add"
                setInfoTelefonos={setInfoTelefonos}
                setTelefonosFijos={setTelefonosFijos}
                setTelefonosMoviles={setTelefonosMoviles}
                onTelefonoAdded={onTelefonoAdded}
              />
            </Space>
          </Tabs.TabPane>
          {/* ----------------------------- CORREOS ----------------------------- */}
          <Tabs.TabPane tab={tabTitle("Correos")} key="4" disabled={!hasSavedPersona}>
            <Space direction="vertical" style={{ width: "100%" }}>
              {infoCorreos?.map((correo) => (
                <CorreoForm
                  key={correo.CorreoID}
                  correo={correo}
                  personaEditing={personaEditing}
                  type="edit"
                  setInfoCorreos={setInfoCorreos}
                  setCorreosPersona={setCorreosPersona}
                />
              ))}
              <CorreoForm
                personaEditing={personaEditing}
                type="add"
                setInfoCorreos={setInfoCorreos}
                setCorreosPersona={setCorreosPersona}
                onCorreoAdded={onCorreoAdded}
              />
            </Space>
          </Tabs.TabPane>
        </Tabs>
      </Modal>
    </>
  );
}